// ========================= LocalForm.jsx =========================
import React, { useEffect, useState, useRef, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getCabs } from "../../../../../store/Cab/CabAction"
import GooglePlacesAutocomplete from "react-google-places-autocomplete"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../../utils/placeDetails"
import { isSameLocation } from "../../../../../utils/locationValidation"
import { getDefaultOptions, getDefaultOptionsSync } from "../../../../../utils/defaultCityOptions"
import { saveRecentSearch, saveRecentTrip } from "../../../../../utils/recentSearches"
import { formatMobileDisplay, parseMobileInput, isValidMobileNumber } from "../../../../../utils/mobileNumberUtils"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../../components/DateTime"
import { loadGoogleMaps } from "../../../../../utils/loadGoogleMaps"
import { geocodeCity } from "../../../../../utils/geocodeCity"

const makeCityOption = (cityName) => ({
  label: cityName,
  value: { description: `${cityName}, India`, place_id: null },
})

const LocalForm = ({ chipPrefill }) => {
  useEffect(() => {
    loadGoogleMaps()
  }, [])
  const [formValues, setFormValues] = useState({
    from: null,
    to: null,
    pickUpDate: "",
    pickUpTime: "",
    mobileNumber: "",
  })

  const extractCityFromText = (text = "") => (text.split(",")[0] || "").trim()

  const [defaultFromOptions, setDefaultFromOptions] = useState(() =>
    getDefaultOptionsSync("from")
  )
  const [defaultToOptions, setDefaultToOptions] = useState(() =>
    getDefaultOptionsSync("to")
  )
  const [fromMenuIsOpen, setFromMenuIsOpen] = useState(false)
  const [toMenuIsOpen, setToMenuIsOpen] = useState(false)

  const [locationDetails, setLocationDetails] = useState({
    pickup_lat: null,
    pickup_long: null,
    pickup_city: "",
    pickup_fullAddress: "",
    drop_lat: null,
    drop_long: null,
    drop_city: "",
    drop_fullAddress: "",
  })

  const routerLocation = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { location } = useSelector((state) => state.location)
  const [searchLoading, setSearchLoading] = useState(false)

  const fromSelectRef = useRef(null)
  const toSelectRef = useRef(null)
  const mobileNumberInputRef = useRef(null)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)

  const locationInitializedRef = useRef(false)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")

  const getLabel = (v) => {
    if (!v) return ""
    if (typeof v === "string") return v
    return v?.fullAddress || v?.label || ""
  }

  const dispatchTripDataUpdated = () => {
    window.dispatchEvent(new Event("tripDataUpdated"))
  }

  const readTrip = () => {
    try {
      return JSON.parse(localStorage.getItem("tripData") || "{}")
    } catch {
      return {}
    }
  }

  const writeTrip = (patch) => {
    const prev = readTrip()
    const next = { ...prev, ...patch }
    // remove undefined keys
    Object.keys(next).forEach((k) => next[k] === undefined && delete next[k])
    localStorage.setItem("tripData", JSON.stringify(next))
    dispatchTripDataUpdated()
  }

  const clearTripKeys = (keys) => {
    const prev = readTrip()
    keys.forEach((k) => delete prev[k])
    localStorage.setItem("tripData", JSON.stringify(prev))
    dispatchTripDataUpdated()
  }

  const resolveCityToPlaceOption = useCallback((cityText) => {
    if (!cityText) return Promise.resolve(null)
    return Promise.resolve({
      label: cityText,
      value: { description: cityText },
    })
  }, [])

  // Autofill FROM on homepage or cities page (your old behavior)
  useEffect(() => {
    locationInitializedRef.current = false

    const run = async () => {
      if (locationInitializedRef.current) return

      if (routerLocation.pathname === "/" && location?.city) {
        const option = await resolveCityToPlaceOption(location.city)
        if (option) {
          locationInitializedRef.current = true
          setFormValues((prev) => ({ ...prev, from: option }))
        }
        return
      }

      if (routerLocation.pathname?.startsWith("/cities/")) {
        const prefill = localStorage.getItem("pulpit_prefill_from_city")
        if (prefill) {
          const option = await resolveCityToPlaceOption(prefill)
          if (option) {
            locationInitializedRef.current = true
            setFormValues((prev) => ({ ...prev, from: option }))
          }
        }
      }
    }

    run()
  }, [routerLocation.pathname, location?.city, resolveCityToPlaceOption])

  // Chip prefill (recent search chip clicked)
  useEffect(() => {
    if (!chipPrefill || chipPrefill.trip_type !== 1) return
    setFormValues((prev) => ({
      ...prev,
      from: makeCityOption(chipPrefill.fromCity),
      to: makeCityOption(chipPrefill.toCity),
    }))
  }, [chipPrefill])

  // Default options (suggestions)
  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.from?.city || formValues?.from?.label
      const fromCity = formValues?.from?.city || formValues?.from?.label

      const fromOptions = await getDefaultOptions("from", currentCity)
      const toOptions = await getDefaultOptions("to", null, fromCity || currentCity)

      setDefaultFromOptions(fromOptions || [])
      setDefaultToOptions(toOptions || [])
    }

    loadDefaultOptions()
  }, [location?.city, formValues?.from])

  const makeValueWithDetails = (value, details) => {
    const labelText = getLabel(value)
    return {
      ...value,
      latitude: details?.lat ?? null,
      longitude: details?.lng ?? null,
      city: details?.city || extractCityFromText(labelText),
      fullAddress: details?.fullAddress || labelText,
    }
  }

  const handlePlaceSelect = async (value, field) => {
    // clear
    if (!value) {
      setFormValues((prev) => ({ ...prev, [field]: null }))

      if (field === "from") {
        setLocationDetails((prev) => ({
          ...prev,
          pickup_lat: null,
          pickup_long: null,
          pickup_city: "",
          pickup_fullAddress: "",
        }))
        clearTripKeys(["pickup", "pickup_lat", "pickup_long", "pickup_city"])
        // also clear TO if From removed (safer)
        setFormValues((prev) => ({ ...prev, to: null }))
        setLocationDetails((prev) => ({
          ...prev,
          drop_lat: null,
          drop_long: null,
          drop_city: "",
          drop_fullAddress: "",
        }))
        clearTripKeys(["drop", "drop_lat", "drop_long", "drop_city"])
      } else {
        setLocationDetails((prev) => ({
          ...prev,
          drop_lat: null,
          drop_long: null,
          drop_city: "",
          drop_fullAddress: "",
        }))
        clearTripKeys(["drop", "drop_lat", "drop_long", "drop_city"])
      }

      return
    }

    // same from/to validation
    if (field === "to" && formValues?.from && isSameLocation?.(value, formValues.from)) {
      setFormValues((prev) => ({ ...prev, to: null }))
      clearTripKeys(["drop", "drop_lat", "drop_long", "drop_city"])
      toast.error("Pickup and drop locations cannot be the same")
      return
    }

    const placeId = value?.value?.place_id || value?.place_id
    const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
    const nextValue = makeValueWithDetails(value, details)

    setFormValues((prev) => ({ ...prev, [field]: nextValue }))

    // recents (same as your other forms)
    saveRecentSearch(nextValue, field)

    if (field === "from") {
      setLocationDetails((prev) => ({
        ...prev,
        pickup_lat: nextValue.latitude,
        pickup_long: nextValue.longitude,
        pickup_city: nextValue.city,
        pickup_fullAddress: nextValue.fullAddress,
      }))

      // store ONLY strings/numbers in tripData (no react-select object)
      writeTrip({
        pickup: nextValue.fullAddress || nextValue.label,
        pickup_lat: nextValue.latitude,
        pickup_long: nextValue.longitude,
        pickup_city: nextValue.city || "",
      })

      // reload TO defaults based on FROM city
      const toOptions = await getDefaultOptions("to", null, nextValue.city || location?.city)
      setDefaultToOptions(toOptions || [])

      // menu behavior (fix overlap bug)
      setFromMenuIsOpen(false)
      setToMenuIsOpen(true)

      // focus to input
      setTimeout(() => toSelectRef.current?.focus?.(), 120)
    } else {
      setLocationDetails((prev) => ({
        ...prev,
        drop_lat: nextValue.latitude,
        drop_long: nextValue.longitude,
        drop_city: nextValue.city,
        drop_fullAddress: nextValue.fullAddress,
      }))

      writeTrip({
        drop: nextValue.fullAddress || nextValue.label,
        drop_lat: nextValue.latitude,
        drop_long: nextValue.longitude,
        drop_city: nextValue.city || "",
      })

      setToMenuIsOpen(false)

      // open date picker
      setTimeout(() => pickUpDateRef.current?.open?.(), 150)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log("LocalForm: handleSubmit triggered")

    if (!formValues.from) {
      console.log("LocalForm: Missing 'from'")
      return toast.error("Please select a pickup location")
    }
    if (!formValues.to) {
      console.log("LocalForm: Missing 'to'")
      return toast.error("Please select a drop location")
    }
    if (isSameLocation?.(formValues.from, formValues.to)) {
      console.log("LocalForm: Same pickup/drop")
      return toast.error("Pickup and drop locations cannot be the same")
    }
    if (!formValues.pickUpDate) {
      console.log("LocalForm: Missing date")
      return toast.error("Please select pickup date")
    }
    if (!formValues.pickUpTime) {
      console.log("LocalForm: Missing time")
      return toast.error("Please select pickup time")
    }
    if (!formValues.mobileNumber) {
      console.log("LocalForm: Missing mobile")
      return toast.error("Please enter mobile number")
    }
    if (!isValidMobileNumber(formValues.mobileNumber)) {
      console.log("LocalForm: Invalid mobile", formValues.mobileNumber)
      return toast.error("Enter a valid 10-digit mobile number (cannot start with 0)")
    }

    console.log("LocalForm: Validation passed")

    const pickup_city =
      locationDetails.pickup_city || formValues.from?.city || extractCityFromText(getLabel(formValues.from)) || ""

    const drop_city =
      locationDetails.drop_city || formValues.to?.city || extractCityFromText(getLabel(formValues.to)) || ""

    if (!pickup_city || !drop_city) {
      console.log("LocalForm: Missing city names", { pickup_city, drop_city })
      return toast.error("Please select valid From/To locations")
    }

    const tripData = {
      trip_type: 1,
      round_trip: 0,

      pickup: locationDetails.pickup_fullAddress || getLabel(formValues.from) || "",
      drop: locationDetails.drop_fullAddress || getLabel(formValues.to) || "",

      pickup_lat: locationDetails.pickup_lat,
      pickup_long: locationDetails.pickup_long,
      drop_lat: locationDetails.drop_lat,
      drop_long: locationDetails.drop_long,

      pickup_city,
      drop_city,

      package_type_in_hours: "",
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: formValues.mobileNumber,
      ip_address: location?.ip || "8425",
    }

    setSearchLoading(true)
    const safetyTimeout = setTimeout(() => {
      setSearchLoading(false)
    }, 15000) // 15s safety

    try {
      console.log("LocalForm submitting tripData:", tripData)
      const response = await dispatch(getCabs(tripData)).unwrap()
      clearTimeout(safetyTimeout)
      console.log("getCabs response:", response)
      
      const cabList = response?.data?.data || []
      if (cabList.length === 0) {
        toast.info("No cabs available for the selected route/time.")
      }

      localStorage.setItem("tripData", JSON.stringify(tripData))
      localStorage.setItem("cabSearchResult", JSON.stringify(response?.data ?? {}))
      dispatchTripDataUpdated()

      const buildQueryParam = (k, v) => {
        if (v === null || v === undefined || v === "") return ""
        return `${k}=${encodeURIComponent(v)}`
      }

      const queryParamsArray = [
        `trip_type=${tripData.trip_type}`,
        `round_trip=${tripData.round_trip}`,
        buildQueryParam("pickup", tripData.pickup),
        buildQueryParam("drop", tripData.drop),
        buildQueryParam("pickup_lat", tripData.pickup_lat),
        buildQueryParam("pickup_long", tripData.pickup_long),
        buildQueryParam("drop_lat", tripData.drop_lat),
        buildQueryParam("drop_long", tripData.drop_long),
        buildQueryParam("pickup_city", tripData.pickup_city),
        buildQueryParam("drop_city", tripData.drop_city),
        buildQueryParam("trip_date", tripData.trip_date),
        buildQueryParam("trip_time", tripData.trip_time),
        buildQueryParam("phone_number", tripData.phone_number),
      ].filter(Boolean)

      saveRecentTrip({ trip_type: 1, fromCity: pickup_city, toCity: drop_city })
      navigate(`/cabs?${queryParamsArray.join("&")}`)
    } catch (err) {
      console.error("LocalForm search failed:", err)
      const msg = err && typeof err === "string" ? err : (err?.message || "Something went wrong")
      const isRateLimit =
        typeof msg === "string" &&
        (msg.includes("Rate limit") || msg.includes("Maximum"))
      toast.error(
        isRateLimit
          ? "You've exceeded the request limit. Please try again after a few hours."
          : msg
      )
    } finally {
      setSearchLoading(false)
    }
  }

  return (
    <div className="local-form">
      <form onSubmit={handleSubmit}>
        <GooglePlacesAutocomplete

          debounce={400}
          minLengthAutocomplete={3}
          selectProps={{
            ref: fromSelectRef,
            value: formValues.from,
            onChange: (v) => handlePlaceSelect(v, "from"),
            placeholder: "From *",
            isClearable: true,

            defaultOptions: defaultFromOptions,
            // menuIsOpen: toMenuIsOpen,
            onFocus: () => {
              setFromMenuIsOpen(true)
              setToMenuIsOpen(false)
            },
            onBlur: () => setFromMenuIsOpen(false),
            onMenuClose: () => setFromMenuIsOpen(false),
            onInputChange: () => {
              // typing should show suggestions
              setFromMenuIsOpen(true)
              setToMenuIsOpen(false)
            },

            getOptionLabel: (e) => e?.label || "",
            getOptionValue: (e) =>
              e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",

            styles: {
              control: (p) => ({
                ...p,
                padding: "5px",
                border: "1px solid #000",
                boxShadow: "none",
                width: "100%",
                height: "4.4rem",
                minHeight: "4.4rem",
                display: "flex",
                alignItems: "center",
                marginBottom: "1.5rem",
                fontSize: "1.4rem",
              }),
              input: (p) => ({ ...p, color: "black", fontSize: "1.4rem" }),
              placeholder: (p) => ({ ...p, color: "black", fontSize: "1.4rem" }),
              menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3 }),
              option: (p, s) => ({
                ...p,
                fontSize: "1.4rem",
                backgroundColor: s.isSelected ? "#f0f0f0" : "white",
                color: "black",
              }),
            },
            components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
          }}
          autocompletionRequest={{ componentRestrictions: { country: "in" } }}
        />

        <GooglePlacesAutocomplete

          debounce={400}
          minLengthAutocomplete={3}
          selectProps={{
            ref: toSelectRef,
            value: formValues.to,
            onChange: (v) => handlePlaceSelect(v, "to"),
            placeholder: "To *",
            isClearable: true,

            defaultOptions: defaultToOptions,
            // menuIsOpen: toMenuIsOpen,
            onFocus: () => {
              setToMenuIsOpen(true)
              setFromMenuIsOpen(false)
            },
            onBlur: () => setToMenuIsOpen(false),
            onMenuClose: () => setToMenuIsOpen(false),
            onInputChange: () => {
              setToMenuIsOpen(true)
              setFromMenuIsOpen(false)
            },

            getOptionLabel: (e) => e?.label || "",
            getOptionValue: (e) =>
              e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",

            styles: {
              control: (p) => ({
                ...p,
                padding: "5px",
                border: "1px solid #000",
                boxShadow: "none",
                width: "100%",
                height: "4.4rem",
                minHeight: "4.4rem",
                display: "flex",
                alignItems: "center",
                marginBottom: "1.5rem",
                fontSize: "1.4rem",
              }),
              input: (p) => ({ ...p, color: "black", fontSize: "1.4rem" }),
              placeholder: (p) => ({ ...p, color: "black", fontSize: "1.4rem" }),
              menu: (p) => ({ ...p, fontSize: "1.4rem", zIndex: 3 }),
              option: (p, s) => ({
                ...p,
                fontSize: "1.4rem",
                backgroundColor: s.isSelected ? "#f0f0f0" : "white",
                color: "black",
              }),
            },
            components: { DropdownIndicator: () => null, IndicatorSeparator: () => null },
          }}
          autocompletionRequest={{ componentRestrictions: { country: "in" } }}
        />

        <DateTimeProvider>
          <div className="local-form__date-time mb-5">
            <DatePickerInput
              ref={pickUpDateRef}
              value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              minDate={new Date(formattedToday)}
              placeholder="Pick Up Date"
              onChange={(d) => {
                setFormValues((prev) => ({
                  ...prev,
                  pickUpDate: d ? new Date(d).toLocaleDateString("en-CA") : "",
                }))
                setTimeout(() => pickUpTimeRef.current?.open?.(), 200)
              }}
            />

            <TimePickerInput
              ref={pickUpTimeRef}
              value={formValues.pickUpTime}
              placeholder="Pick Up Time"
              selectedDate={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
              onChange={(t) => {
                setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))
                setTimeout(() => mobileNumberInputRef.current?.focus?.(), 150)
              }}
            />
          </div>
        </DateTimeProvider>

        <div className="input-phone-wrapper mb-5">
          <div
            className={`input-phone-box${isValidMobileNumber(formValues.mobileNumber) ? " input-phone-box--valid" : formValues.mobileNumber.length > 0 ? " input-phone-box--invalid" : ""}`}
          >
            <span className="phone-code">+91</span>
            <span className="phone-divider"></span>

            <input
              type="tel"
              className="phone-input"
              placeholder="Mobile Number"
              name="mobileNumber"
              value={formatMobileDisplay(formValues.mobileNumber)}
              onChange={(e) => {
                const v = parseMobileInput(e.target.value)
                setFormValues((p) => ({ ...p, mobileNumber: v }))
              }}
              maxLength={11}
              ref={mobileNumberInputRef}
              required
            />
          </div>
        </div>

        <button type="submit" className="input-submit-button" disabled={searchLoading}>
          {searchLoading ? "Searching..." : "Search"}
        </button>
      </form>

      <p className="local-form__paragraph" style={{ color: "red" }}>
        Local Cabs are unavailable for distances exceeding 40 kilometers.
      </p>
    </div>
  )
}

export default LocalForm
